import type { SupportedLocale } from "../i18n-locales";

export type BusinessSession = {
  id: "morning" | "evening";
  opens: string;
  closes: string;
};

const WEEKDAY_SESSIONS: readonly BusinessSession[] = [
  { id: "morning", opens: "09:30", closes: "13:00" },
  { id: "evening", opens: "17:00", closes: "20:30" },
];

const SATURDAY_SESSIONS: readonly BusinessSession[] = [
  { id: "morning", opens: "09:30", closes: "13:00" },
];

export const BUSINESS_TIME_ZONE = "Europe/Rome";

const HOURS_TEXT: Record<SupportedLocale, string> = {
  it: "Lun–Ven 09:30–13:00 / 17:00–20:30 · Sab 09:30–13:00 · Dom chiuso",
  en: "Mon–Fri 09:30–13:00 / 17:00–20:30 · Sat 09:30–13:00 · Sun closed",
  es: "Lun–Vie 09:30–13:00 / 17:00–20:30 · Sáb 09:30–13:00 · Dom cerrado",
  fr: "Lun–Ven 09:30–13:00 / 17:00–20:30 · Sam 09:30–13:00 · Dim fermé",
  de: "Mo–Fr 09:30–13:00 / 17:00–20:30 · Sa 09:30–13:00 · So geschlossen",
};

function minutes(value: string): number | null {
  const match = /^(\d{2}):(\d{2})$/.exec(value);
  if (!match) return null;
  const hours = Number(match[1]);
  const mins = Number(match[2]);
  if (hours > 23 || mins > 59) return null;
  return hours * 60 + mins;
}

export function businessHoursText(locale: SupportedLocale): string {
  return HOURS_TEXT[locale];
}

export function businessHoursForDate(
  isoDate: string,
): readonly BusinessSession[] {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(isoDate)) return [];
  const date = new Date(`${isoDate}T12:00:00.000Z`);
  if (Number.isNaN(date.getTime())) return [];
  const day = date.getUTCDay();
  if (day === 0) return [];
  if (day === 6) return SATURDAY_SESSIONS;
  return WEEKDAY_SESSIONS;
}

export function isWithinBusinessHours(
  isoDate: string,
  opens: string,
  closes: string,
): boolean {
  const start = minutes(opens);
  const end = minutes(closes);
  if (start === null || end === null || end <= start) return false;
  return businessHoursForDate(isoDate).some((session) => {
    const sessionStart = minutes(session.opens);
    const sessionEnd = minutes(session.closes);
    return (
      sessionStart !== null &&
      sessionEnd !== null &&
      start >= sessionStart &&
      end <= sessionEnd
    );
  });
}

export function openingHoursSpecification(): Array<Record<string, unknown>> {
  const weekdays = [
    "https://schema.org/Monday",
    "https://schema.org/Tuesday",
    "https://schema.org/Wednesday",
    "https://schema.org/Thursday",
    "https://schema.org/Friday",
  ];
  return [
    ...WEEKDAY_SESSIONS.map((session) => ({
      "@type": "OpeningHoursSpecification",
      dayOfWeek: weekdays,
      opens: session.opens,
      closes: session.closes,
    })),
    {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: "https://schema.org/Saturday",
      opens: SATURDAY_SESSIONS[0].opens,
      closes: SATURDAY_SESSIONS[0].closes,
    },
  ];
}
